import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import useWebSocket from 'react-use-websocket'
import clsx from 'clsx'

import { NotificationsAPI } from '@makyoapp/makyo-sdk'
import { axiosSiteDataConfig } from '@makyoapp/makyo-sdk/api/api'
import { notificationsWSURL } from '@makyoapp/makyo-sdk/ws/Notifications'
import { baseWSURL } from '@makyoapp/makyo-sdk/ws/ws'
import { abbreviateNumber, NotificationItemAdapter } from '@makyoapp/makyo-common'
import { Check16, ChevronDown, ChevronUp, Eye24, Notif24 } from '@makyoapp/makyo-icon/icon'
import { DD, DDItem, ShadowVariant, Twemoji } from '@makyoapp/makyo-ui'

import { useNotificationsContext } from '@/context/NotificationsContext'
import useAuth from '@/hooks/useAuth'
import { useReqError } from '@/hooks/useReqError'
import { USER_NOTIFICATION_ROUTE, USER_ROUTE } from '@/shared/constants'

import { NotifModal } from '../AppModals/NotifModal'

/**
 * handle showing the notification menu for toolbar
 */
export function NotificationMenu() {
  const { t } = useTranslation()
  const { userData } = useAuth()
  const printAPIError = useReqError()

  const { unreadCount, notifications, setNotifications, updateRead, isLoading, setUnreadCount, update } =
    useNotificationsContext()

  const [readNotif, setReadNotif] = useState<NotificationItemAdapter | undefined>(undefined)
  const [isModalVisible, setIsModalVisible] = useState(false)

  const url = userData ? `${baseWSURL}${notificationsWSURL}` : ''

  const { sendMessage, lastMessage, readyState } = useWebSocket(url, {
    onOpen: () => {
      const mes = {
        _id: 'auth',
        CreatedAt: new Date(),
        URL: '/auth',
        Headers: { 'user-token': axiosSiteDataConfig.headers['user-token'] },
        ContentType: 'text/plain',
        Status: 'ok',
        StatusCode: 200,
        Body: '',
      }
      sendMessage(JSON.stringify(mes))
      //sendMessage('on open call')
    },
    shouldReconnect: () => true,
  })

  const [, setMessageHistory] = useState([])

  useEffect(() => {
    if (lastMessage) {
      setMessageHistory((prev) => prev.concat(lastMessage.data))
      try {
        const lastData = JSON.parse(lastMessage.data)
        update()
      } catch (e: unknown) {
        printAPIError({
          status: 0,
          message: `${e}`,
        })
      }
    }
  }, [lastMessage, update, printAPIError])

  const handleNotificationClick = (item: NotificationItemAdapter) => {
    console.log('Notification clicked:', item)
    setReadNotif(item)
    setIsModalVisible(true) // Show the modal immediately

    // Mark as read if it is not already read
    if (!item.isRead) {
      NotificationsAPI.ReqRead({
        _Notifs: [item.id],
      })
        .then(() => {
          const [readCount] = updateRead([item.id])
          setUnreadCount(unreadCount - readCount)
        })
        .catch((e) => {})
    }
  }

  return (
    <DD
      menuAlwaysRender={false}
      id='notif-menu'
      className='w-full h-full'
      menuClassName='dd-menu'
      withNotch={true}
      notchClassName={'bg-slate-900 mk-notch-pos'}
      placement={'bottom-end'}
      toggle={(active) => (
        <div className='relative flex-c bell h-full'>
          <Notif24 />
          {unreadCount > 0 && <span className={clsx('notif-count')}>{abbreviateNumber(unreadCount, 0)}</span>}
          <div>{!active ? <ChevronDown /> : <ChevronUp />}</div>
        </div>
      )}
      toggleClassName='group dd-menu-toggle'
      menuNoPad={true}
      menuShadow={ShadowVariant.XL}
      menuWrapperClassName={clsx('pb-2 mk-scroll')}
      menuAfter={
        <>
          <hr className='dd-menu-hr' />
          <div className=''>
            <Link className='dd-menu-item' to={`${USER_ROUTE}/${USER_NOTIFICATION_ROUTE}`}>
              <div className='flex-cc w-full'>
                <div>
                  <Eye24 width={16} height={16} />
                </div>
                <div>{t('View all notifications')}</div>
              </div>
            </Link>
            {unreadCount > 0 && (
              <>
                <hr className='dd-menu-hr' />
                <DDItem
                  id='notif-menu-mark-all-read'
                  className='dd-menu-item'
                  onClick={() => {
                    NotificationsAPI.ReqReadAll()
                      .then(() => {
                        setNotifications(
                          notifications.map((nt) => {
                            nt.isRead = true
                            return nt
                          })
                        )
                        setUnreadCount(0)
                      })
                      .catch((e) => {
                        printAPIError(e)
                      })
                  }}
                >
                  <div className='flex-cc w-full'>
                    <div>
                      <Check16 />
                    </div>
                    <div>{t('Mark all as read')}</div>
                  </div>
                </DDItem>
              </>
            )}
          </div>
        </>
      }
    >
      {isLoading ? (
        <DDItem className='flex-cc w-full'>
          <span className='loading spin' />
        </DDItem>
      ) : notifications.length > 0 ? (
        <>
          {notifications.map((item) => {
            return (
              <DDItem
                key={item.id}
                noPadY={true}
                className='dd-menu-row'
                onClick={(e) => {
                  e.stopPropagation() // Prevent event propagation
                  handleNotificationClick(item)
                }}
              >
                <div className='dd-menu-item-notif' data-trigger-id={item.trigger}>
                  <div className='shrink-0 py-1'>
                    <Twemoji className='p-0 w-5 h-5 shrink-0 notif-icon' emoji={item.icon} />
                  </div>
                  <div className='txt'>
                    <div className={clsx(item.isRead ? 'toolbar-text-light' : '', 'txt', 'notif-title')}>
                      {item.title}
                    </div>
                    <div className='toolbar-text-light text-m-sm txt notif-content'>
                      {item.content?.replaceAll(/<[^>]*>?/gm, ' ')}
                    </div>
                  </div>
                </div>
              </DDItem>
            )
          })}

          {isModalVisible && readNotif && (
            <NotifModal
              show
              notification={readNotif}
              onHide={() => {
                setIsModalVisible(false)
                setReadNotif(undefined)
              }}
            />
          )}
        </>
      ) : (
        <DDItem>
          <div className='toolbar-text-light'>{t('No notifications found.')}</div>
        </DDItem>
      )}
    </DD>
  )
}
